import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

export function Reveal({ children, delay = 0, y = 32, className }) {
    return (
        <motion.div
            className={className}
            initial={{ opacity: 0, y }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-70px" }}
            transition={{ duration: 0.75, delay, ease: [0.22, 1, 0.36, 1] }}
        >
            {children}
        </motion.div>
    );
}

export function KineticText({ text, className, wordClassName = "", delay = 0, as: Tag = "span" }) {
    const words = text.split(" ");
    return (
        <Tag className={className} aria-label={text}>
            {words.map((word, i) => (
                <span key={i} className="inline-block overflow-hidden align-bottom pt-[0.22em] -mt-[0.22em] pb-[0.16em] -mb-[0.16em]">
                    <motion.span
                        className={`inline-block will-change-transform ${wordClassName}`}
                        initial={{ y: "118%" }}
                        animate={{ y: 0 }}
                        transition={{ duration: 0.85, delay: delay + i * 0.07, ease: [0.22, 1, 0.36, 1] }}
                    >
                        {word}
                        {i < words.length - 1 ? " " : ""}
                    </motion.span>
                </span>
            ))}
        </Tag>
    );
}

export function Counter({ value, suffix = "", decimals = 0 }) {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-40px" });
    const [display, setDisplay] = useState(0);

    useEffect(() => {
        if (!inView) return;
        const start = performance.now();
        const duration = 1600;
        let raf;
        const tick = (now) => {
            const p = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - p, 3);
            setDisplay(value * eased);
            if (p < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [inView, value]);

    return (
        <span ref={ref}>
            {display.toFixed(decimals)}
            {suffix}
        </span>
    );
}

export function Eyebrow({ children }) {
    return (
        <p className="mb-4 flex items-center gap-3 font-mono text-xs font-medium uppercase tracking-[0.24em] text-neon">
            <span className="inline-block h-px w-8 bg-neon/60" aria-hidden="true" />
            {children}
        </p>
    );
}
